import React, { useEffect, useMemo, useState } from 'react';
import {
  ABDM_CONSENT_PURPOSE,
  DEFAULT_HI_TYPES,
  HI_TYPES_OPTIONS,
  defaultExpiryDate,
  defaultExpiryTime,
  defaultFromDate,
  defaultToDate,
  type ConsentPurpose,
} from './abdmConsentConstants';
import { initConsent, getConsentErrorMessage, isRetryableConsentError } from '../../custom/services/abdmConsentService';

export type RequestConsentDrawerProps = {
  open: boolean;
  onClose: () => void;
  onRequested: (sessionId?: string) => void;
  notify?: (opts: { title: string; message: string; type: 'success' | 'error' | 'info' }) => void;
};

type FormState = {
  abhaAddress: string;
  drName: string;
  fromDate: string;
  toDate: string;
  expiryDate: string;
  expiryTime: string;
  purposeCode: keyof typeof ABDM_CONSENT_PURPOSE;
  hiTypes: string[];
};

function createInitialForm(): FormState {
  return {
    abhaAddress: '',
    drName: '',
    fromDate: defaultFromDate(),
    toDate: defaultToDate(),
    expiryDate: defaultExpiryDate(),
    expiryTime: defaultExpiryTime(),
    purposeCode: 'CAREMGT',
    hiTypes: [...DEFAULT_HI_TYPES],
  };
}

/** Build ISO-8601 expiry with explicit local timezone offset (required by RIS adapter). */
function buildExpiryIso(expiryDate: string, expiryTime: string): string {
  const local = new Date(`${expiryDate}T${expiryTime || '23:59'}:00`);
  if (Number.isNaN(local.getTime())) {
    return `${expiryDate}T${expiryTime || '23:59'}:00Z`;
  }
  const pad = (n: number) => String(Math.floor(Math.abs(n))).padStart(2, '0');
  const offsetMin = -local.getTimezoneOffset();
  const sign = offsetMin >= 0 ? '+' : '-';
  const hh = pad(offsetMin / 60);
  const mm = pad(offsetMin % 60);
  const yyyy = local.getFullYear();
  const mo = pad(local.getMonth() + 1);
  const dd = pad(local.getDate());
  const h = pad(local.getHours());
  const mi = pad(local.getMinutes());
  const ss = pad(local.getSeconds());
  return `${yyyy}-${mo}-${dd}T${h}:${mi}:${ss}${sign}${hh}:${mm}`;
}

function validateForm(form: FormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!form.abhaAddress.trim()) {
    errors.abhaAddress = 'ABHA address is required';
  } else if (!/^[a-zA-Z0-9._-]{3,}@[a-zA-Z0-9.-]+$/.test(form.abhaAddress.trim())) {
    errors.abhaAddress = 'ABHA address format looks invalid';
  }
  if (!form.drName.trim()) {
    errors.drName = 'Requesting clinician is required';
  } else if (form.drName.trim().length > 200) {
    errors.drName = 'Requesting clinician name is too long';
  }
  const today = defaultToDate();
  if (form.fromDate > today) {
    errors.fromDate = 'From date cannot be in the future';
  }
  if (form.toDate > today) {
    errors.toDate = 'To date cannot be in the future';
  }
  if (form.fromDate && form.toDate && form.fromDate > form.toDate) {
    errors.fromDate = 'From date must be on or before to date';
    errors.toDate = 'To date must be on or after from date';
  }
  const expiry = new Date(`${form.expiryDate}T${form.expiryTime || '23:59'}:00`);
  if (Number.isNaN(expiry.getTime()) || expiry.getTime() <= Date.now()) {
    errors.expiryDate = 'Consent expiry must be in the future';
  }
  if (!form.hiTypes.length) {
    errors.hiTypes = 'Select at least one health information type';
  }
  return errors;
}

export default function RequestConsentDrawer({
  open,
  onClose,
  onRequested,
  notify,
}: RequestConsentDrawerProps) {
  const [form, setForm] = useState<FormState>(createInitialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Clear PHI leftovers when the drawer closes (shared workstations).
  useEffect(() => {
    if (!open) {
      setForm(createInitialForm());
      setErrors({});
      setSubmitting(false);
    }
  }, [open]);

  const purpose: ConsentPurpose = ABDM_CONSENT_PURPOSE[form.purposeCode];

  const hiTypeOptions = useMemo(() => [...HI_TYPES_OPTIONS], []);

  if (!open) {
    return null;
  }

  const update = (patch: Partial<FormState>) => {
    setForm(prev => {
      const next = { ...prev, ...patch };
      setErrors(validateForm(next));
      return next;
    });
  };

  const toggleHiType = (type: string) => {
    const next = form.hiTypes.includes(type)
      ? form.hiTypes.filter(t => t !== type)
      : [...form.hiTypes, type];
    update({ hiTypes: next });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors = validateForm(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    let purposePayload: ConsentPurpose & { btgAcknowledged?: boolean } = { ...purpose };
    if (form.purposeCode === 'BTG') {
      const acknowledged = window.confirm(
        'Break the Glass grants emergency access and is audited. Confirm you have a valid emergency need to request this consent.'
      );
      if (!acknowledged) {
        return;
      }
      purposePayload = { ...purposePayload, btgAcknowledged: true };
    }

    setSubmitting(true);
    try {
      const expiryDate = buildExpiryIso(form.expiryDate, form.expiryTime);
      const result = await initConsent({
        abhaAddress: form.abhaAddress.trim(),
        drName: form.drName.trim(),
        fromDate: form.fromDate,
        toDate: form.toDate,
        hiTypes: form.hiTypes,
        expiryDate,
        purpose: purposePayload,
      });
      notify?.({
        title: 'Consent requested',
        message: 'Waiting for the patient to approve in their PHR app.',
        type: 'success',
      });
      onRequested(result.consentSessionId);
      onClose();
    } catch (err) {
      const message = getConsentErrorMessage(err, 'Failed to submit consent request');
      const retryable = isRetryableConsentError(err);
      notify?.({
        title: retryable ? 'Request failed — retry' : 'Request failed',
        message: retryable
          ? `${message} This looks temporary — you can retry shortly.`
          : message,
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="abha-consent-drawer-backdrop" role="presentation" onClick={onClose}>
      <aside
        className="abha-consent-drawer"
        role="dialog"
        aria-labelledby="abha-consent-drawer-title"
        onClick={ev => ev.stopPropagation()}
      >
        <header className="abha-consent-drawer__header">
          <h2 id="abha-consent-drawer-title">Request Consent</h2>
          <button type="button" className="abha-consent-btn abha-consent-btn--ghost" onClick={onClose}>
            Close
          </button>
        </header>

        <form className="abha-consent-drawer__body" onSubmit={handleSubmit}>
          <label className="abha-consent-field">
            <span>ABHA address</span>
            <input
              type="text"
              value={form.abhaAddress}
              onChange={e => update({ abhaAddress: e.target.value })}
              placeholder="patient@sbx"
              autoComplete="off"
            />
            {errors.abhaAddress ? <em>{errors.abhaAddress}</em> : null}
          </label>

          <label className="abha-consent-field">
            <span>Requesting clinician</span>
            <input
              type="text"
              value={form.drName}
              onChange={e => update({ drName: e.target.value })}
              placeholder="Enter clinician name"
              autoComplete="off"
            />
            {errors.drName ? <em>{errors.drName}</em> : null}
          </label>

          <div className="abha-consent-field-row">
            <label className="abha-consent-field">
              <span>From date</span>
              <input
                type="date"
                value={form.fromDate}
                onChange={e => update({ fromDate: e.target.value })}
              />
              {errors.fromDate ? <em>{errors.fromDate}</em> : null}
            </label>
            <label className="abha-consent-field">
              <span>To date</span>
              <input
                type="date"
                value={form.toDate}
                onChange={e => update({ toDate: e.target.value })}
              />
              {errors.toDate ? <em>{errors.toDate}</em> : null}
            </label>
          </div>

          <div className="abha-consent-field-row">
            <label className="abha-consent-field">
              <span>Consent expiry date</span>
              <input
                type="date"
                value={form.expiryDate}
                onChange={e => update({ expiryDate: e.target.value })}
              />
              {errors.expiryDate ? <em>{errors.expiryDate}</em> : null}
            </label>
            <label className="abha-consent-field">
              <span>Expiry time</span>
              <input
                type="time"
                value={form.expiryTime}
                onChange={e => update({ expiryTime: e.target.value })}
              />
            </label>
          </div>

          <label className="abha-consent-field">
            <span>Purpose</span>
            <select
              value={form.purposeCode}
              onChange={e =>
                update({ purposeCode: e.target.value as keyof typeof ABDM_CONSENT_PURPOSE })
              }
            >
              {Object.entries(ABDM_CONSENT_PURPOSE).map(([code, value]) => (
                <option key={code} value={code}>
                  {value.text}
                </option>
              ))}
            </select>
            {form.purposeCode === 'BTG' ? (
              <em style={{ color: '#b45309' }}>
                Break the Glass requires confirmation on submit and is audited.
              </em>
            ) : null}
          </label>

          <fieldset className="abha-consent-field">
            <legend>Health information types</legend>
            <div className="abha-consent-hi-types">
              {hiTypeOptions.map(type => (
                <label key={type} className="abha-consent-check">
                  <input
                    type="checkbox"
                    checked={form.hiTypes.includes(type)}
                    onChange={() => toggleHiType(type)}
                  />
                  <span>{type}</span>
                </label>
              ))}
            </div>
            {errors.hiTypes ? <em>{errors.hiTypes}</em> : null}
          </fieldset>

          <footer className="abha-consent-drawer__footer">
            <button
              type="button"
              className="abha-consent-btn abha-consent-btn--ghost"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button type="submit" className="abha-consent-btn abha-consent-btn--primary" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit consent request'}
            </button>
          </footer>
        </form>
      </aside>
    </div>
  );
}
